<template>
  <div :style="loginStyle" class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0 justify-content-center">
      <b-col lg="11" class="pt-5 pl-3">
        <InitialHeader :onlyLogo="true" />
      </b-col>
      <b-col
          sm="12"
          md="12"
          lg="11"
          class="d-flex align-items-center mb-5"
      >
        <b-col
            sm="12"
            md="10"
            lg="4"
            class="mx-auto auth-bg p-2 rounded"
        >
          <div id="bem-vindo-colaborador" class="px-xl-3 pb-1 pt-3">
            <h2 class="mb-1 auth-title">
              Bem-vindo ao
              <span class="lt-p1 titles-color">vacina</span>
              <span class="lt-p2 titles-color">sesi</span>
            </h2>
            <p class="text-muted mb-1">
              Entre com CPF e senha definitiva para visualizar sua carteira de vacinação.
            </p>
          </div>

          <div id="acessar" class="px-xl-3 pt-1 pb-3">
            <b-alert
                v-if="errorMessage"
                show
                fade
                class="mb-2"
                variant="danger"
            >
              <div class="custom-alert">
                <span>{{ errorMessage }}</span>
              </div>
            </b-alert>

            <validation-observer ref="loginRules">
              <b-form class="auth-login-form mt-2" @submit.prevent="login">
              <b-form-group label="CPF" label-for="login-cpf">
                <validation-provider
                  #default="{ errors }"
                  name="cpf"
                  rules="required|cpfValidator"
                >
                  <vue-cleave
                      id="login-cpf"
                      v-model="form.cpf"
                      :raw="false"
                      :options="cpfMask"
                      class="form-control"
                      placeholder="000.000.000-00"
                      name="login-cpf"
                      autocomplete="username"
                      :class="{ 'is-invalid': errors.length > 0 }"
                      @focus="errorMessage = ''"
                  />
                  <small v-if="errors[0]" class="text-danger">
                    {{ errors[0] }}
                  </small>
                </validation-provider>
              </b-form-group>

              <b-form-group>
                <div class="d-flex justify-content-between">
                  <label for="login-password">Senha</label>
                  <b-link :to="{name:'public-forgot-password'}">
                    <small class="titles-color">Esqueci minha senha</small>
                  </b-link>
                </div>

                <b-input-group class="input-group-merge">
                  <b-form-input
                      id="login-password"
                      v-model="form.senha"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="******"
                      autocomplete="current-password"
                      @focus="errorMessage = ''"
                      required
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>

              <button
                  :disabled="loading"
                  type="submit"
                  class="btn button-form btn-block"
              >
                {{ loading ? 'Entrando...' : 'Entrar' }}
              </button>
              </b-form>
            </validation-observer>
          </div>
        </b-col>
      </b-col>
      <FooterInitial />
    </b-row>
  </div>
</template>

<script>
import {
  BAlert,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  BRow,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required } from '@validations'
import { validatorCPF } from '@core/utils/validations/validators'

extend('cpfValidator', {
  validate: validatorCPF,
  message: 'CPF inválido',
})
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import VueCleave from 'vue-cleave-component'
import FooterInitial from '@/views/components/custom/footer/FooterInitial.vue'
import InitialHeader from '@/views/components/custom/page-header/InitialHeader.vue'

export default {
  name: 'PublicLogin',
  components: {
    BAlert,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BLink,
    BRow,
    ValidationProvider,
    ValidationObserver,
    VueCleave,
    FooterInitial,
    InitialHeader,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      required,
      cpfMask: {
        numericOnly: true,
        delimiters: ['.', '.', '-'],
        blocks: [3, 3, 3, 2],
      },
      form: {
        cpf: '',
        senha: '',
      },
      loading: false,
      errorMessage: '',
      loginStyle: {
        'background-image': `url(${require('@/assets/custom-images/bg/bg-login.png')})`,
        'background-repeat': 'no-repeat',
        'background-position': 'center center',
        'background-size': 'cover',
      },
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    limparCpf(value) {
      return value ? value.replaceAll(/\D/g, '') : ''
    },
    async login() {
      this.errorMessage = ''
      
      const isValid = await this.$refs.loginRules.validate()
      if (!isValid) {
        return
      }

      const cpf = this.limparCpf(this.form.cpf)
      const senha = this.form.senha?.trim()

      this.loading = true
      try {
        const response = await this.$store.dispatch('publicAccess/login', {
          cpf,
          senha,
        })

        if (response.data?.primeiro_acesso) {
          this.$router.push({ name: 'public-first-access' })
        } else {
          this.$router.push({ name: 'public-carteira' })
        }
      } catch (error) {
        const status = error?.response?.status
        const message = error?.response?.data?.message
        const errors = error?.response?.data?.errors

        if (!error?.response) {
          this.errorMessage = 'Não foi possível conectar. Tente novamente.'
        } else if (status === 429) {
          this.errorMessage = message || 'Conta temporariamente bloqueada por tentativas inválidas. Tente novamente mais tarde.'
        } else if (status === 422 && errors) {
          const errorMessages = Object.values(errors).flat()
          this.errorMessage = errorMessages.join(' ')
        } else {
          this.errorMessage = message || 'CPF ou senha inválidos.'
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>